/**
 * Design tab — the authored view of `globalVariables`.
 *
 * Custom Variables shows every value a snapshot holds, typed only by `typeof`.
 * This shows the ones a design declares, with the control each one deserves: a
 * swatch for a colour, a slider for a 0–1 knob, portrait/landscape rows for an
 * orientation pair. The design lives beside the snapshots as its own file and
 * is shared by every snapshot a flow generates from the same template.
 *
 * SAME DATA, DIFFERENT VIEW. Every edit here writes exactly where Custom
 * Variables writes — `playEngineConfig.globalVariables.<path>` — so the two tabs
 * cannot disagree, and a value the design does not mention is still reachable
 * from the raw tab. A design narrows what you see; it never owns the data.
 *
 * ISOLATED ON PURPOSE. Nothing here is imported by the playEngine or scene
 * editor panels and nothing is imported from them (`designFields.js` carries
 * its own chrome). Three product modes share this screen and the sibling panels
 * must stay changeable without dragging this along.
 */

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import RefreshIcon from "@material-ui/icons/Refresh";
import GenerateIcon from "@material-ui/icons/Build";

import { sendCommand } from "../../modules/playEngineBridge";
import {
  assetIdFor,
  getAcceptString,
  getAssetMapFromConfig,
  isPairDepType,
} from "../../modules/playEngineAssetUtils";
import { optimizeSourceForSnapshot } from "../../modules/snapshotOptimize";
import * as previewQueue from "../../modules/playEnginePreviewQueue";
import {
  BASE_LANGUAGE,
  applyGlobalVariableOverrides,
  isOverrideLanguage,
  switchPlayEngineLanguage,
} from "../../modules/playEngineLocalization";
import { usePlayEngineGlobalVariables } from "../../modules/playEngineGlobalVariablesStore";
import {
  addField,
  addGroup,
  designFieldsByPath,
  designHasFieldPath,
  ensureGroupIds,
  fieldAtPath,
  moveField,
  moveGroup,
  designCoverage,
  groupContents,
  mergeSnapshotSettings,
  normalizeDesign,
  pruneDesignToSnapshot,
  removeField,
  removeGroup,
  reorderField,
  renameGroup,
  scaffoldDesign,
  schemaOptionsByKey,
  schemaOptionsForPath,
  updateField,
  validateDesign,
  walkGroups,
} from "../../modules/playEngineDesign";
import {
  getSnapshotDesign,
  listSnapshotDesigns,
  isSnapshotEditorBase,
  saveSnapshotDesign,
} from "../../modules/snapshotEditorApi";
import {
  getPlayEngineDesign,
  savePlayEngineDesign,
} from "../../modules/playEngineDesignApi";
import {
  BAR_BG,
  DESIGN_PANEL_CSS,
  DESIGN_SYNC_DEBOUNCE_MS,
  PANEL_BG,
  SECTION_DIVIDER,
  SectionHeader,
  setDesignPreviewIframeId,
} from "./designFields";
import { thumbnailFromEntry } from "./designAssetSource";
import DesignFieldRenderer from "./designFieldRenderer";
import DesignOutliner from "./designOutliner";
import DialogAddDesignField, { parseOptionLines } from "./dialogAddDesignField";
import PlayEngineTabSearch, { matchesTabSearch } from "../playEngineTabSearch";

/** Where the split between outliner and inspector sits, remembered per user. */
const SPLIT_STORAGE_KEY = "playEngineDesignSplit";
const DEFAULT_SPLIT = 0.34;
const MIN_SPLIT = 0.15;
const MAX_SPLIT = 0.75;

function readStoredSplit() {
  try {
    const stored = Number(window.localStorage.getItem(SPLIT_STORAGE_KEY));
    if (isFinite(stored) && stored >= MIN_SPLIT && stored <= MAX_SPLIT) return stored;
  } catch (_err) {
    // A browser refusing storage is not a reason to fail to render.
  }
  return DEFAULT_SPLIT;
}

function SplitHandle({ onPointerDown }) {
  return (
    <div
      onPointerDown={onPointerDown}
      style={{
        flex: "0 0 auto",
        height: 6,
        cursor: "row-resize",
        backgroundColor: "#2b2b2b",
        borderTop: "1px solid #1f1f1f",
        borderBottom: "1px solid #1f1f1f",
      }}
    />
  );
}

function InspectorHeader({ title }) {
  return (
    <div
      style={{
        position: "sticky",
        top: 0,
        zIndex: 2,
        display: "flex",
        alignItems: "baseline",
        gap: 8,
        padding: "10px 12px",
        backgroundColor: BAR_BG,
        borderBottom: `1px solid ${SECTION_DIVIDER}`,
      }}
    >
      <span style={{ fontSize: 13, fontWeight: 600, color: "#e8e8e8" }}>{title}</span>
    </div>
  );
}

/**
 * The bar that names the pane below it.
 *
 * The Scene Editor puts one over its hierarchy and another over its inspector,
 * and it is what makes the split read as two panes rather than one list that
 * happens to have a seam in it. This tab had the lower one only, so the tree sat
 * loose under the toolbar with nothing framing it.
 */
function PaneCaption({ title, children }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "8px 12px",
        flexShrink: 0,
        backgroundColor: BAR_BG,
        borderTop: `1px solid ${SECTION_DIVIDER}`,
        borderBottom: `1px solid ${SECTION_DIVIDER}`,
      }}
    >
      <span style={{ fontSize: 13, fontWeight: 600, color: "#e8e8e8" }}>{title}</span>
      <span style={{ flex: 1 }} />
      {children}
    </div>
  );
}

/** A Scene Editor pane button: a small flat chip, not a toolbar icon. */
const PANE_BUTTON_STYLE = {
  color: "#aaa",
  fontSize: "11px",
  textTransform: "none",
  minWidth: 0,
  padding: "2px 8px",
  backgroundColor: BAR_BG,
  borderRadius: 4,
};

function unwrapDesignResponse(response) {
  if (response && response.ok === false) {
    const error = new Error("DESIGN_REQUEST_FAILED");
    error.designError = response.error;
    error.status = response.status;
    throw error;
  }
  return _.get(response, "data", response);
}

function designErrorCode(error) {
  return (
    _.get(error, "designError.error") ||
    _.get(error, "designError") ||
    _.get(error, "response.data.error") ||
    _.get(error, "response.data") ||
    null
  );
}

export default function containerPlayEngineDesign(props) {
  const baseID = _.get(props, "selectedBase._id");
  const fileName = _.get(props, "selectedVersion._id");
  const canEdit = _.get(props, "userPermissions.editContent") !== false;
  // Which half of a responsive value the rows edit — the preview's own
  // orientation, the same flag the Scene Editor and the version settings read.
  // Absent (a host that has no orientation toggle) means portrait.
  const isPortrait = props.newSetPortrait !== false;

  // A number scrub takes the pointer away from the page; the preview iframe
  // would otherwise swallow it the moment the drag crossed the seam.
  useEffect(() => {
    setDesignPreviewIframeId(props.iFrameId);
  }, [props.iFrameId]);

  // The enums the engine declares for its component config keys. Asked once per
  // preview: a design row whose value the engine constrains is then a dropdown,
  // with no list typed into the design by hand.
  const [engineOptions, setEngineOptions] = useState(() => new Map());

  // The engine answers this only once it has installed its command listener,
  // which is usually after this panel mounts — hence the retries rather than a
  // single attempt. A preview that never answers simply leaves the rows as the
  // design describes them.
  useEffect(() => {
    let cancelled = false;
    let timer = null;

    function attempt(retriesLeft) {
      sendCommand(props.iFrameId, { type: "playengine:get-dependencies" })
        .then((response) => {
          if (cancelled) return;
          setEngineOptions(schemaOptionsByKey(_.get(response, "data", [])));
        })
        .catch(() => {
          if (cancelled || retriesLeft <= 0) return;
          timer = setTimeout(() => attempt(retriesLeft - 1), 2000);
        });
    }

    attempt(4);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [props.iFrameId, fileName]);
  const usesSnapshotStorage = isSnapshotEditorBase(props.selectedBase);
  // Only a snapshot picks between designs: a playEngine version keeps its own
  // beside it.
  const canBindDesign = usesSnapshotStorage && typeof props.bindSnapshotDesign === "function";

  const [rawDesign, setRawDesign] = useState(null);
  const [designId, setDesignId] = useState(null);
  // Needed again now that the tree can be edited: a save quotes the hash it read
  // so a design changed on disk since is refused rather than flattened.
  const [contentHash, setContentHash] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [renaming, setRenaming] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [generating, setGenerating] = useState(false);

  // Every design on the flow, for the picker that repoints this snapshot at
  // another one. Snapshot storage only: a playEngine version keeps its design
  // beside the version and has nothing to pick between.
  const [flowDesigns, setFlowDesigns] = useState([]);
  // Rows the snapshot has no value for are out of the way by default; see
  // `shownDesign`.
  const [showMissingFields, setShowMissingFields] = useState(false);
  // The binding before the last change, so it can be taken back in one click.
  const [previousDesignId, setPreviousDesignId] = useState(null);
  // Set when the user says to open a design this snapshot barely matches.
  const [ignoreMismatch, setIgnoreMismatch] = useState(false);
  // Whether the picker also lists the designs this snapshot barely matches.
  const [showAllDesigns, setShowAllDesigns] = useState(false);
  const [mergingSnapshotSettings, setMergingSnapshotSettings] = useState(false);
  // { id, label, contents } while the "delete everything in it?" question is up.
  const [removingGroup, setRemovingGroup] = useState(null);
  const [binding, setBinding] = useState(false);

  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [search, setSearch] = useState("");
  const [addingField, setAddingField] = useState(false);
  // The path of the row whose descriptor is being edited — the path rather than
  // the field, so the form follows the design if it is rewritten underneath.
  const [editingFieldPath, setEditingFieldPath] = useState(null);
  const [splitRatio, setSplitRatio] = useState(readStoredSplit);
  const [availHeight, setAvailHeight] = useState(520);

  const rootRef = useRef(null);
  const outlinerPaneRef = useRef(null);
  const inspectorPaneRef = useRef(null);
  // The drag reads the ratio it started from. A ref, not the state, so the
  // handler installed once on pointerdown is not holding a stale closure.
  const splitRatioRef = useRef(splitRatio);
  splitRatioRef.current = splitRatio;
  // Loading and generating both await the network, and the panel can be gone by
  // the time they come back — a snapshot switch, or the tab being closed. React
  // warns about a state update on an unmounted component; this is the guard.
  const isMountedRef = useRef(true);
  useEffect(() => () => { isMountedRef.current = false; }, []);
  const fieldWriteTimersRef = useRef(new Map());
  const pendingFieldWritesRef = useRef(new Map());
  const flushAllFieldWritesRef = useRef(() => Promise.resolve([]));

  const selectedLanguage = props.localizationLanguage || BASE_LANGUAGE;

  // ── The shared live values ──────────────────────────────────────────────
  // Custom Variables and Design subscribe to one cache per iframe. The first
  // panel loads the engine; the other receives the same object without another
  // postMessage round trip, and edits/runtime pushes update both immediately.
  const {
    variables,
    overridesByLanguage,
    setVariable: setSharedVariable,
    setOverride: setSharedOverride,
    clearOverrides: clearSharedOverrides,
  } = usePlayEngineGlobalVariables({
    iFrameId: props.iFrameId,
    isPreviewLoaded: props.isPreviewLoaded !== false,
    selectedVersion: props.selectedVersion,
  });

  const overrides = useMemo(
    () => _.get(overridesByLanguage, selectedLanguage, {}) || {},
    [overridesByLanguage, selectedLanguage]
  );

  const effectiveVariables = useMemo(
    () => applyGlobalVariableOverrides(variables, overrides),
    [variables, overrides]
  );

  // ── The asset slots ─────────────────────────────────────────────────────
  // The same map the Assets tab edits, in both product modes: in the snapshot
  // editor `selectedVersion.playEngineConfig` IS the snapshot file. Read from
  // props rather than cached, so an undo — which rewrites the config under the
  // panel — simply arrives as new props and the rows re-read.
  const assetsMap = useMemo(
    () => getAssetMapFromConfig(_.get(props, "selectedVersion.playEngineConfig")),
    [props.selectedVersion]
  );
  const assetsMapRef = useRef(assetsMap);
  assetsMapRef.current = assetsMap;

  // Whether THIS editor can put bytes in a slot. The snapshot editor hands down
  // a provider (its library and its uploader); the version editor deliberately
  // does not, because its media library lives in its own Assets tab — so there
  // the row offers the jump instead of a picker.
  const assetProviderRef = useRef(null);
  assetProviderRef.current = props.assetProvider || null;
  const canUploadAssets = Boolean(_.get(props, "assetProvider.uploadAsset"));
  const canPickAssetsFromLibrary = Boolean(_.get(props, "assetProvider.pickFromLibrary"));

  const [busyAssetKey, setBusyAssetKey] = useState(null);

  /**
   * Thumbnails for slots whose bytes are not in the config.
   *
   * A version's asset holds `@server:<id>`, and the media records behind those
   * ids are not threaded into this panel. The preview has them all loaded
   * though, so ONE `get-assets` answers every row at once — per snapshot, and
   * only when a row actually needs it.
   */
  const [previewAssetSources, setPreviewAssetSources] = useState({});
  const previewAssetsRequestedRef = useRef(null);

  // Dropped when the snapshot changes, and when the host says the config was
  // rewritten underneath us — an undo of a replace must not leave the new
  // picture on screen next to the restored bytes.
  useEffect(() => {
    setPreviewAssetSources({});
    previewAssetsRequestedRef.current = null;
  }, [fileName, props.externalConfigRevision]);

  const availableLanguages = useMemo(() => {
    const languages = _.get(
      props,
      "selectedVersion.playEngineConfig.localization.availableLanguages",
      [BASE_LANGUAGE]
    );
    return Array.isArray(languages) && languages.length ? languages : [BASE_LANGUAGE];
  }, [props.selectedVersion]);

  const design = useMemo(() => normalizeDesign(rawDesign), [rawDesign]);
  const problems = useMemo(() => (rawDesign ? validateDesign(rawDesign) : []), [rawDesign]);
  const fieldCount = useMemo(() => (design ? designFieldsByPath(design).size : 0), [design]);

  // What this snapshot can actually show. One design serves many snapshots and a
  // flow serves more than one game, so a design pointed at the wrong snapshot
  // would list another game's components — see pruneDesignToSnapshot. Turning
  // the switch off is for AUTHORING a design that is slightly ahead of the
  // snapshot in front of you.
  const shownDesign = useMemo(
    () =>
      showMissingFields
        ? design
        : pruneDesignToSnapshot(design, { variables, assets: assetsMap }),
    [assetsMap, design, showMissingFields, variables]
  );

  // Enough of a design has to belong to this snapshot for the design to be worth
  // showing at all. Below this the tab shows what it found instead of a panel
  // full of another game's components — see the mismatch screen.
  const MIN_COVERAGE = 0.5;

  const coverage = useMemo(
    () => designCoverage(design, { variables, assets: assetsMap }),
    [assetsMap, design, variables]
  );

  /**
   * The designs worth offering for THIS snapshot.
   *
   * A flow generates for more than one game, so most of its designs describe
   * components this snapshot does not have. Below the same bar the panel uses,
   * a design is not an option — except the one already bound, which has to stay
   * selectable to be seen, and any design the server could not measure.
   */
  /**
   * ONE ROW PER KIND OF DESIGN. Forty snapshots of one game leave forty design
   * files that describe the same values — the picker is a choice between
   * DESCRIPTIONS, not between files, so identical ones collapse into the one
   * that is bound (or, failing that, the closest match).
   */
  function collapseToKinds(entries) {
    const byShape = new Map();
    entries.forEach((entry) => {
      const key = entry.shapeKey || `id:${entry.designId}`;
      const known = byShape.get(key);
      if (!known) {
        byShape.set(key, { ...entry, sameShapeCount: 1 });
        return;
      }

      known.sameShapeCount += 1;
      const beatsKnown =
        entry.designId === designId ||
        (known.designId !== designId &&
          (entry.matchRatio || 0) > (known.matchRatio || 0));
      if (beatsKnown) {
        byShape.set(key, { ...entry, sameShapeCount: known.sameShapeCount });
      }
    });
    return Array.from(byShape.values());
  }

  const designsByFit = useMemo(() => {
    const belongs = (entry) =>
      entry.designId === designId ||
      !_.isFinite(entry.matchRatio) ||
      entry.matchRatio >= MIN_COVERAGE;

    return {
      fitting: collapseToKinds(flowDesigns.filter(belongs)),
      // Kept, not dropped. The coverage bar is a heuristic and it measures
      // design -> snapshot: a rich design over a half-built snapshot of its OWN
      // game reads low, and hiding it outright leaves the right answer
      // unreachable. So the poor matches go below the line rather than away, and
      // the panel's own "this design does not match" screen stays the real
      // guard.
      poor: collapseToKinds(flowDesigns.filter((entry) => !belongs(entry))),
    };
  }, [designId, flowDesigns]);

  const offeredDesigns = showAllDesigns
    ? [...designsByFit.fitting, ...designsByFit.poor]
    : designsByFit.fitting;

  const hiddenDesignCount = designsByFit.poor.length;
  const isBoundDesignKnown = Boolean(
    designId && flowDesigns.some((entry) => entry.designId === designId)
  );

  const isMismatched =
    canBindDesign && coverage.total > 0 && coverage.ratio < MIN_COVERAGE && !ignoreMismatch;

  const shownFieldCount = useMemo(
    () => (shownDesign ? designFieldsByPath(shownDesign).size : 0),
    [shownDesign]
  );

  const allEntries = useMemo(() => walkGroups(shownDesign), [shownDesign]);
  const matchingGroupNames = useMemo(() => new Set(allEntries
    .filter((entry) => search.trim() && matchesTabSearch(entry.group.label, search))
    .map((entry) => entry.group.id)), [allEntries, search]);
  const matchingEntries = useMemo(() => search.trim() ? allEntries.filter((entry) =>
    matchingGroupNames.has(entry.group.id) ||
    entry.ancestors.some((id) => matchingGroupNames.has(id)) ||
    entry.group.fields.some((field) =>
      matchesTabSearch(field.label, search) || matchesTabSearch(field.path, search)
    )
  ) : allEntries, [allEntries, search, matchingGroupNames]);
  const matchingGroupIds = useMemo(() => {
    const ids = new Set();
    matchingEntries.forEach((entry) => {
      ids.add(entry.group.id);
      entry.ancestors.forEach((id) => ids.add(id));
    });
    return ids;
  }, [matchingEntries]);
  const groupCount = useMemo(() => walkGroups(design).length, [design]);
  const shownGroupCount = allEntries.length;

  const selectedGroup = useMemo(() => {
    const found = allEntries.find((entry) =>
      entry.group.id === selectedGroupId && (!search.trim() || matchingEntries.includes(entry))
    );
    return found ? found.group : _.get(matchingEntries, "0.group", null);
  }, [allEntries, matchingEntries, search, selectedGroupId]);

  /** A child row shows only when every one of its ancestors is open. */
  const visibleEntries = useMemo(
    () => search.trim()
      ? allEntries.filter((entry) => matchingGroupIds.has(entry.group.id))
      : allEntries.filter((entry) => entry.ancestors.every((id) => expandedIds.has(id))),
    [allEntries, expandedIds, matchingGroupIds, search]
  );

  // ── Loading ────────────────────────────────────────────────────────────

  const loadFlowDesigns = useCallback(async () => {
    if (!canBindDesign || !baseID) return;
    try {
      const response = await listSnapshotDesigns(baseID, fileName);
      const designs = _.get(unwrapDesignResponse(response), "designs", []);
      if (!isMountedRef.current) return;
      setFlowDesigns(Array.isArray(designs) ? designs : []);
    } catch (_err) {
      // The picker simply has nothing to offer; the tab itself still works.
      if (isMountedRef.current) setFlowDesigns([]);
    }
  }, [baseID, canBindDesign, fileName]);

  const loadDesign = useCallback(async () => {
    if (!baseID || !fileName) return;
    setLoading(true);
    setLoadError(null);
    try {
      const response = usesSnapshotStorage
        ? await getSnapshotDesign(baseID, fileName)
        : await getPlayEngineDesign(fileName);
      const body = unwrapDesignResponse(response);
      const loaded = _.get(body, "design", null);
      // Ids are pinned once, here: a hand-written design need not carry them,
      // and a drag has to be able to name what it moved.
      if (!isMountedRef.current) return;
      setRawDesign(loaded ? ensureGroupIds(loaded) : null);
      setDesignId(_.get(body, "designId", null));
      setContentHash(_.get(body, "contentHash", null));
    } catch (err) {
      if (!isMountedRef.current) return;
      setRawDesign(null);
      setContentHash(null);
      setLoadError(
        designErrorCode(err) || "Could not load the design."
      );
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, [baseID, fileName, usesSnapshotStorage]);

  useEffect(() => {
    loadDesign();
  }, [loadDesign]);

  useEffect(() => {
    loadFlowDesigns();
  }, [loadFlowDesigns]);

  /**
   * Point this snapshot at another design, or at none.
   *
   * The binding is `metadata.design` on the SNAPSHOT, so the write goes back to
   * whoever owns the file; this panel only re-reads afterwards. Detaching does
   * not delete anything: the design file stays, and the snapshot falls back to
   * one named after it — usually none, which is the "no design yet" screen.
   */
  const handleBindDesign = useCallback(
    async (nextDesignId) => {
      if (!canBindDesign || binding) return;
      if ((nextDesignId || null) === (designId || null)) return;

      setBinding(true);
      const cameFrom = designId || null;
      try {
        await props.bindSnapshotDesign(nextDesignId || null);
        setPreviousDesignId(cameFrom);
        setIgnoreMismatch(false);
        await loadDesign();
        await loadFlowDesigns();
        if (props.createSnackbar) {
          props.createSnackbar(
            nextDesignId
              ? `This snapshot now uses the "${nextDesignId}" design.`
              : "Design detached. The design file itself is untouched.",
            "success"
          );
        }
      } catch (err) {
        if (props.createSnackbar) {
          props.createSnackbar(designErrorCode(err) || "Could not change the design.", "error");
        }
      } finally {
        if (isMountedRef.current) setBinding(false);
      }
    },
    [binding, canBindDesign, designId, loadDesign, loadFlowDesigns, props]
  );

  // A different snapshot means a different design; do not keep pointing at a
  // group that may not exist in the next one.
  useEffect(() => {
    setSelectedGroupId(null);
  }, [fileName]);

  // ── Writing ────────────────────────────────────────────────────────────

  const commitFieldWrite = useCallback(
    (writeKey) => {
      const timer = fieldWriteTimersRef.current.get(writeKey);
      if (timer) clearTimeout(timer);
      fieldWriteTimersRef.current.delete(writeKey);

      const pending = pendingFieldWritesRef.current.get(writeKey);
      if (!pending) return Promise.resolve();
      pendingFieldWritesRef.current.delete(writeKey);

      const { path, value, language, asOverride } = pending;

      // Design and Custom Variables are two views of the same live value. Move
      // their shared cache update into the same commit as persistence so hidden
      // panels do not rebuild their trees for every pointer-move event.
      if (asOverride) {
        // Override maps are keyed by literal dotted paths, so persist the whole
        // language map. Writing a dotted sub-path would change its shape.
        const nextOverrides = setSharedOverride(language, path, value);
        props.setSelectedVersionPathValue({
          path: `playEngineConfig.localization.globalVariableOverrides.${language}`,
          value: nextOverrides,
        });
      } else {
        setSharedVariable(path, value);
        props.setSelectedVersionPathValue({
          path: `playEngineConfig.globalVariables.${path}`,
          value,
        });
      }

      // Queued rather than awaited: the preview does not need to acknowledge a
      // value change, and a burst on one field then costs a single message.
      previewQueue.push(props.iFrameId, previewQueue.previewKey.variable(language, path), {
        type: "playengine:set-global-variable",
        key: path,
        value,
        ...(asOverride ? { language } : {}),
      });
      return Promise.resolve();
    },
    [props.iFrameId, props.setSelectedVersionPathValue, setSharedOverride, setSharedVariable]
  );

  const flushAllFieldWrites = useCallback(
    () => Promise.all([...pendingFieldWritesRef.current.keys()].map(commitFieldWrite)),
    [commitFieldWrite]
  );
  flushAllFieldWritesRef.current = flushAllFieldWrites;

  // The controls own their transient display value. Everything observable by
  // the rest of the dashboard is committed together after the gesture settles.
  const handleChange = useCallback(
    (path, value) => {
      if (!canEdit) return;

      const language = selectedLanguage;
      const writeKey = `${language}:${path}`;
      pendingFieldWritesRef.current.set(writeKey, {
        path,
        value,
        language,
        asOverride: isOverrideLanguage(language),
      });

      const existing = fieldWriteTimersRef.current.get(writeKey);
      if (existing) clearTimeout(existing);
      fieldWriteTimersRef.current.set(
        writeKey,
        setTimeout(() => commitFieldWrite(writeKey), DESIGN_SYNC_DEBOUNCE_MS)
      );
    },
    [canEdit, commitFieldWrite, selectedLanguage]
  );

  const handleCommit = useCallback(
    (path) => commitFieldWrite(`${selectedLanguage}:${path}`),
    [commitFieldWrite, selectedLanguage]
  );

  const handleRevert = useCallback(
    (path) => {
      if (!canEdit) return;

      // Revert wins over a still-local edit to the same field.
      const writeKey = `${selectedLanguage}:${path}`;
      const queued = fieldWriteTimersRef.current.get(writeKey);
      if (queued) {
        clearTimeout(queued);
        fieldWriteTimersRef.current.delete(writeKey);
      }
      pendingFieldWritesRef.current.delete(writeKey);

      const { languageMap: nextOverrides, removed: overriddenPaths } =
        clearSharedOverrides(selectedLanguage, path);
      props.setSelectedVersionPathValue({
        path: `playEngineConfig.localization.globalVariableOverrides.${selectedLanguage}`,
        value: nextOverrides,
      });

      // Anything queued for these fields belongs to the value being reverted, so
      // it goes out first rather than landing on top of the revert.
      previewQueue.flush(props.iFrameId);

      for (const overriddenPath of overriddenPaths) {
        sendCommand(props.iFrameId, {
          type: "playengine:clear-global-variable-override",
          key: overriddenPath,
          language: selectedLanguage,
        });
      }
    },
    [canEdit, clearSharedOverrides, props, selectedLanguage]
  );

  /**
   * Stable identities for the three callbacks a field row receives.
   *
   * The rows are memoised, and a memo only holds if every prop it is compared on
   * is stable. `handleChange` and its siblings are rebuilt whenever anything
   * they close over changes — including `props.setSelectedVersionPathValue`,
   * which the version editor rebuilds on every one of its own renders. Routing
   * through a ref keeps the identity fixed for the life of the panel while the
   * behaviour stays current.
   */
  const handlersRef = useRef(null);
  handlersRef.current = { handleChange, handleCommit, handleRevert };

  const onFieldChange = useCallback((path, value) => handlersRef.current.handleChange(path, value), []);
  const onFieldCommit = useCallback((path) => handlersRef.current.handleCommit(path), []);
  const onFieldRevert = useCallback((path) => handlersRef.current.handleRevert(path), []);

  // ── Replacing an asset ──────────────────────────────────────────────────
  //
  // Deliberately NOT routed through the debounced field pipeline above: that
  // queue exists to coalesce a scrub gesture into one write and to steer a
  // value into a language override. A replace is one discrete choice carrying
  // megabytes, and it belongs to the asset every language shares.

  /**
   * Put new bytes in a slot: config first, then the preview.
   *
   * The whole entry is written at once rather than `source` and `sourceRef`
   * separately — two writes would be two undo steps, with a state in between
   * where the new bytes sit next to a reference to the old ones.
   */
  const replaceAsset = useCallback(
    async (assetKey, source) => {
      if (!canEdit || !source) return;
      const entry = assetsMapRef.current[assetKey];
      if (!entry) return;

      // Anything a field row still owes goes out first, so the two writes
      // cannot arrive in the wrong order.
      await flushAllFieldWritesRef.current();

      props.setSelectedVersionPathValue({
        path: `playEngineConfig.assets.${assetKey}`,
        value: { ...entry, source, sourceRef: null },
      });

      // The snapshot editor hands the preview its config once per boot, so this
      // message is the ONLY way a replace becomes visible without a reload.
      try {
        await sendCommand(
          props.iFrameId,
          {
            type: "playengine:set-asset",
            assetId: assetIdFor(entry.type, assetKey),
            source,
          },
          isPairDepType(entry.type) ? 15000 : 5000
        );
      } catch (err) {
        console.warn(`[design] set-asset failed for "${assetKey}": ${err.message}`);
        if (props.createSnackbar) {
          props.createSnackbar(
            `Saved "${assetKey}", but the preview did not take it — reload the preview to see it.`,
            "warning",
            false
          );
        }
      }
    },
    [canEdit, props]
  );

  const handleAssetUpload = useCallback(
    async (assetKey, file) => {
      const entry = assetsMapRef.current[assetKey];
      const provider = assetProviderRef.current;
      if (!entry || !file || !provider || !provider.uploadAsset) return;

      setBusyAssetKey(assetKey);
      try {
        // The provider compresses and keeps the file in the flow's library on
        // the way past, which is what makes this the same act as replacing the
        // asset from the Assets tab rather than a second, quieter one.
        const uploaded = await provider.uploadAsset(file, entry.type);
        const source = _.get(uploaded, "base64");
        if (source) await replaceAsset(assetKey, source);
      } catch (err) {
        console.error("[design] asset upload failed:", err);
        if (props.createSnackbar) {
          props.createSnackbar(`Could not replace "${assetKey}": ${err.message}`, "error", false);
        }
      } finally {
        if (isMountedRef.current) setBusyAssetKey(null);
      }
    },
    [props, replaceAsset]
  );

  const handleAssetPickFromLibrary = useCallback(
    async (assetKey) => {
      const entry = assetsMapRef.current[assetKey];
      const provider = assetProviderRef.current;
      if (!entry || !provider || !provider.pickFromLibrary) return;

      try {
        const picked = await provider.pickFromLibrary(entry.type);
        if (!picked) return;
        setBusyAssetKey(assetKey);
        // The library file itself is never rewritten; this compresses the copy
        // being embedded, exactly as the Assets tab does for the same pick.
        const source = await optimizeSourceForSnapshot({
          baseID,
          assetType: entry.type,
          source: picked,
          fileName: assetKey,
          onSaved: (stats) =>
            props.createSnackbar &&
            props.createSnackbar(
              `Image compressed by ${stats.savedPercent}% for this snapshot.`,
              "success",
              false
            ),
        });
        await replaceAsset(assetKey, source);
      } catch (err) {
        console.error("[design] library pick failed:", err);
        if (props.createSnackbar) {
          props.createSnackbar(`Could not replace "${assetKey}": ${err.message}`, "error", false);
        }
      } finally {
        if (isMountedRef.current) setBusyAssetKey(null);
      }
    },
    [baseID, props, replaceAsset]
  );

  /** Stable identities, for the same reason the field callbacks have them. */
  const assetHandlersRef = useRef(null);
  assetHandlersRef.current = {
    handleAssetUpload,
    handleAssetPickFromLibrary,
    navigate: props.onNavigateToAsset,
  };

  const onAssetUpload = useCallback(
    (assetKey, file) => assetHandlersRef.current.handleAssetUpload(assetKey, file),
    []
  );
  const onAssetPickFromLibrary = useCallback(
    (assetKey) => assetHandlersRef.current.handleAssetPickFromLibrary(assetKey),
    []
  );
  const onNavigateToAsset = useCallback((assetKey) => {
    const navigate = assetHandlersRef.current.navigate;
    if (navigate) navigate(assetKey);
  }, []);

  const handleLanguageSwitch = useCallback(
    async (language) => {
      if (language === selectedLanguage) return;
      await flushAllFieldWrites();
      previewQueue.flush(props.iFrameId);
      await switchPlayEngineLanguage({
        iFrameId: props.iFrameId,
        selectedVersion: props.selectedVersion,
        setSelectedVersionPathValue: props.setSelectedVersionPathValue,
        language,
      });
      if (props.setLocalizationLanguage) props.setLocalizationLanguage(language);
    },
    [flushAllFieldWrites, props, selectedLanguage]
  );

  // Flush rather than discard when this panel, language, or snapshot leaves.
  // This also covers a fast tab switch before the debounce timer expires.
  useEffect(
    () => () => {
      void flushAllFieldWritesRef.current();
    },
    [fileName, selectedLanguage]
  );

  // ── Editing the tree ───────────────────────────────────────────────────
  //
  // Selection, ordering and nesting all live in the design FILE, so every one
  // of these is a write. They are applied optimistically and rolled back on
  // refusal: the alternative is a drag that visibly does nothing until a round
  // trip finishes.

  const persistDesign = useCallback(
    async (nextRaw) => {
      if (!canEdit || !fileName) return;
      const previous = rawDesign;
      const previousHash = contentHash;

      setRawDesign(nextRaw);
      setSaving(true);
      try {
        const response = usesSnapshotStorage
          ? await saveSnapshotDesign(baseID, {
            designId,
            design: nextRaw,
            contentHash: previousHash,
          })
          : await savePlayEngineDesign(fileName, {
            designId,
            design: nextRaw,
            contentHash: previousHash,
          });
        const body = unwrapDesignResponse(response);
        setContentHash(_.get(body, "contentHash", null));
        if (!designId && _.get(body, "designId")) setDesignId(body.designId);
        const designReference = _.get(body, "designReference");
        if (
          !usesSnapshotStorage &&
          designReference &&
          String(_.get(props, "selectedVersion.playEngineConfig.metadata.design", "")) !==
            String(designReference)
        ) {
          props.setSelectedVersionPathValue({
            path: "playEngineConfig.metadata.design",
            value: String(designReference),
          });
        }
      } catch (err) {
        setRawDesign(previous);
        setContentHash(previousHash);
        const code = designErrorCode(err);
        if (props.createSnackbar) {
          props.createSnackbar(
            code === "DESIGN_CHANGED_ON_DISK"
              ? "This design changed on disk — reload before editing it again."
              : `Could not save the design: ${code || "request failed"}`,
            "error"
          );
        }
      } finally {
        setSaving(false);
      }
    },
    [baseID, canEdit, contentHash, designId, fileName, props, rawDesign, usesSnapshotStorage]
  );

  const handleMove = useCallback(
    (draggedId, targetId, position) => {
      const next = moveGroup(rawDesign, draggedId, targetId, position);
      // moveGroup returns the original when the move is impossible — dropping a
      // group into its own subtree, say. Nothing to save, nothing to say.
      if (next === rawDesign) return;
      if (position === "inside") {
        setExpandedIds((current) => new Set(current).add(targetId));
      }
      persistDesign(next);
    },
    [persistDesign, rawDesign]
  );

  const handleAddGroup = useCallback(
    (parentId) => {
      const next = addGroup(rawDesign, "New Group", parentId);
      if (parentId) setExpandedIds((current) => new Set(current).add(parentId));
      persistDesign(next);
    },
    [persistDesign, rawDesign]
  );

  /**
   * Delete a group.
   *
   * An empty one goes straight away. One that holds something asks first and
   * then takes everything under it — emptying a scaffolded group row by row is
   * most of the work of cleaning up a generated design, and the rows it removes
   * are descriptors: the values stay in the snapshot either way.
   */
  const handleRemoveGroup = useCallback(
    (id) => {
      const contents = groupContents(rawDesign, id);
      if (contents.fields > 0 || contents.groups > 0) {
        const entry = walkGroups(design).find((candidate) => candidate.group.id === id);
        setRemovingGroup({ id, label: _.get(entry, "group.label", "this group"), contents });
        return;
      }

      const next = removeGroup(rawDesign, id);
      if (next !== rawDesign) persistDesign(next);
    },
    [design, persistDesign, rawDesign]
  );

  const confirmRemoveGroup = useCallback(() => {
    if (!removingGroup) return;
    const { id, label, contents } = removingGroup;
    setRemovingGroup(null);

    const next = removeGroup(rawDesign, id, { withContents: true });
    if (next === rawDesign) return;
    persistDesign(next);
    if (props.createSnackbar) {
      props.createSnackbar(
        `Removed "${label}" and ${contents.fields} setting${contents.fields === 1 ? "" : "s"} from the design — the values themselves are untouched.`,
        "info"
      );
    }
  }, [persistDesign, props, rawDesign, removingGroup]);

  const handleRenameCommit = useCallback(() => {
    if (!renaming) return;
    const next = renameGroup(rawDesign, renaming.id, renaming.label);
    setRenaming(null);
    if (next !== rawDesign) persistDesign(next);
  }, [persistDesign, rawDesign, renaming]);

  // ── Editing fields ─────────────────────────────────────────────────────
  //
  // Which group a setting sits in, and whether the design describes it at all.
  // Both are writes to the design file and neither touches `globalVariables`:
  // removing a row stops the Design tab describing a value, it does not delete
  // it, and Custom Variables goes on showing it either way.

  const handleMoveField = useCallback(
    (path, groupId) => {
      const next = moveField(rawDesign, path, groupId);
      if (next === rawDesign) return;
      setSelectedGroupId(groupId);
      setExpandedIds((current) => new Set(current).add(groupId));
      persistDesign(next);
    },
    [persistDesign, rawDesign]
  );

  const handleReorderField = useCallback(
    (path, offset) => {
      const next = reorderField(rawDesign, path, offset);
      if (next === rawDesign) return;
      persistDesign(next);
    },
    [persistDesign, rawDesign]
  );

  const handleRemoveField = useCallback(
    (path) => {
      const next = removeField(rawDesign, path);
      if (next === rawDesign) return;
      persistDesign(next);
      if (props.createSnackbar) {
        props.createSnackbar(
          `Removed "${path}" from the design — the value itself is untouched.`,
          "info"
        );
      }
    },
    [persistDesign, props, rawDesign]
  );

  const handleAddField = useCallback(
    (spec, groupId) => {
      const next = addField(rawDesign, groupId, spec);
      setAddingField(false);
      if (next === rawDesign) {
        if (props.createSnackbar) {
          props.createSnackbar("Could not add that setting — check the path and type.", "warning");
        }
        return;
      }
      setSelectedGroupId(groupId);
      setExpandedIds((current) => new Set(current).add(groupId));
      persistDesign(next);
    },
    [persistDesign, props, rawDesign]
  );

  const editingField = useMemo(
    () => (editingFieldPath ? fieldAtPath(rawDesign, editingFieldPath) : null),
    [editingFieldPath, rawDesign]
  );

  const handleUpdateField = useCallback(
    (spec, groupId) => {
      if (!editingField) return;
      const path = editingFieldPath;
      let next = updateField(rawDesign, path, spec);
      if (next === rawDesign) {
        if (props.createSnackbar) {
          props.createSnackbar("Could not save that setting — check the type.", "warning");
        }
        return;
      }
      if (groupId && groupId !== editingField.groupId) {
        next = moveField(next, path, groupId);
        setSelectedGroupId(groupId);
        setExpandedIds((current) => new Set(current).add(groupId));
      }
      setEditingFieldPath(null);
      persistDesign(next);
    },
    [editingField, editingFieldPath, persistDesign, props, rawDesign]
  );

  // ── Allowed values ─────────────────────────────────────────────────────
  //
  // The list a row offers as a dropdown, edited on its own rather than only
  // inside the full "Edit setting" form: turning a text box into a picker is
  // the one change these rows get asked for over and over, and it is not worth
  // a form with a dozen other boxes in it.
  const [optionsDraft, setOptionsDraft] = useState(null);

  const handleEditFieldOptions = useCallback(
    (path) => {
      const located = fieldAtPath(rawDesign, path);
      if (!located) return;
      setOptionsDraft({
        path,
        text: (Array.isArray(located.field.options) ? located.field.options : [])
          .map((option) =>
            option.label === undefined || String(option.label) === String(option.value)
              ? String(option.value)
              : `${option.value} = ${option.label}`
          )
          .join("\n"),
      });
    },
    [rawDesign]
  );

  const handleOptionsCommit = useCallback(() => {
    if (!optionsDraft) return;
    const located = fieldAtPath(rawDesign, optionsDraft.path);
    if (!located) {
      setOptionsDraft(null);
      return;
    }
    const next = updateField(rawDesign, optionsDraft.path, {
      ...located.field,
      options: parseOptionLines(optionsDraft.text),
    });
    setOptionsDraft(null);
    if (next === rawDesign) return;
    persistDesign(next);
  }, [optionsDraft, persistDesign, rawDesign]);

  // Stable for the same reason the three above are: these reach memoised rows.
  const fieldEditRef = useRef(null);
  fieldEditRef.current = {
    handleMoveField,
    handleReorderField,
    handleRemoveField,
    setEditingFieldPath,
    handleEditFieldOptions,
  };

  const onMoveField = useCallback(
    (path, groupId) => fieldEditRef.current.handleMoveField(path, groupId),
    []
  );
  const onRemoveField = useCallback((path) => fieldEditRef.current.handleRemoveField(path), []);
  const onEditField = useCallback((path) => fieldEditRef.current.setEditingFieldPath(path), []);
  const onReorderField = useCallback(
    (path, offset) => fieldEditRef.current.handleReorderField(path, offset),
    []
  );
  const onEditFieldOptions = useCallback(
    (path) => fieldEditRef.current.handleEditFieldOptions(path),
    []
  );

  const isPathTaken = useCallback((path) => designHasFieldPath(rawDesign, path), [rawDesign]);

  const toggleExpanded = useCallback((id) => {
    setExpandedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  /**
   * Describe what this snapshot has and the design does not.
   *
   * Pointing a design at a snapshot leaves two gaps, and pruning only closes
   * one: the settings this snapshot has that the design never heard of stay
   * invisible until they are written into it. This adds them, as the starter
   * generator would have.
   */
  const handleMergeSnapshotSettings = useCallback(async () => {
    if (!canEdit || mergingSnapshotSettings || !rawDesign) return;

    const hidden = _.get(
      props,
      "selectedVersion.playEngineConfig.inspectorConfig.globalVariables.hidden",
      []
    );
    const next = mergeSnapshotSettings(rawDesign, variables, { hiddenPaths: hidden });
    if (next === rawDesign) {
      if (props.createSnackbar) {
        props.createSnackbar("This design already describes everything in this snapshot.", "info");
      }
      return;
    }

    setMergingSnapshotSettings(true);
    try {
      const added = walkGroups(normalizeDesign(next)).length - walkGroups(design).length;
      await persistDesign(next);
      if (props.createSnackbar) {
        props.createSnackbar(
          `Added ${added} group${added === 1 ? "" : "s"} for this snapshot's own settings.`,
          "success"
        );
      }
    } finally {
      if (isMountedRef.current) setMergingSnapshotSettings(false);
    }
  }, [
    canEdit,
    design,
    mergingSnapshotSettings,
    persistDesign,
    props,
    rawDesign,
    variables,
  ]);

  // ── Generating a starter ───────────────────────────────────────────────

  const handleGenerate = useCallback(async () => {
    if (!canEdit || generating) return;
    const targetId =
      designId ||
      (usesSnapshotStorage
        ? String(fileName || "").replace(/\.json$/i, "")
        : String(_.get(props, "selectedVersion.title") || fileName || "design"));
    if (!targetId) return;

    setGenerating(true);
    try {
      const hidden = _.get(
        props,
        "selectedVersion.playEngineConfig.inspectorConfig.globalVariables.hidden",
        []
      );
      const scaffolded = scaffoldDesign(variables, { id: targetId, hiddenPaths: hidden });
      const response = usesSnapshotStorage
        ? await saveSnapshotDesign(baseID, {
          designId: targetId,
          design: scaffolded,
          contentHash: null,
        })
        : await savePlayEngineDesign(fileName, {
          designId: targetId,
          design: scaffolded,
          contentHash: null,
        });
      const body = unwrapDesignResponse(response);
      const designReference = _.get(body, "designReference");
      if (!usesSnapshotStorage && designReference) {
        props.setSelectedVersionPathValue({
          path: "playEngineConfig.metadata.design",
          value: String(designReference),
        });
      }
      await loadDesign();
      if (props.createSnackbar) {
        props.createSnackbar(`Generated a starter design with ${scaffolded.groups.length} groups.`, "success");
      }
    } catch (err) {
      const code = designErrorCode(err);
      if (props.createSnackbar) {
        props.createSnackbar(
          code === "DESIGN_NOT_FOUND" || code === "DESIGN_CHANGED_ON_DISK"
            ? "A design already exists for this snapshot — reload to see it."
            : `Could not generate a design: ${code || "request failed"}`,
          "error"
        );
      }
    } finally {
      if (isMountedRef.current) setGenerating(false);
    }
  }, [baseID, canEdit, designId, fileName, generating, loadDesign, props, usesSnapshotStorage, variables]);

  // ── Layout ─────────────────────────────────────────────────────────────

  useLayoutEffect(() => {
    function measure() {
      const node = rootRef.current;
      if (!node) return;
      const top = node.getBoundingClientRect().top;
      setAvailHeight(Math.max(320, window.innerHeight - top - 8));
    }
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  /**
   * Drag the seam between the two panes.
   *
   * Measured against the two panes and nothing else — the way the Scene Editor
   * does it. The ratio used to be taken from the top of the whole panel, which
   * is not where the outliner starts: the toolbar, and now the pane caption, sit
   * above it. So the seam did not land under the pointer, and it drifted further
   * the more chrome there was above it.
   */
  const startDividerDrag = useCallback(
    (event) => {
      event.preventDefault();
      const outlinerPane = outlinerPaneRef.current;
      const inspectorPane = inspectorPaneRef.current;
      if (!outlinerPane || !inspectorPane) return;

      const startY = event.clientY;
      const splitHeight =
        outlinerPane.getBoundingClientRect().height +
        inspectorPane.getBoundingClientRect().height;
      if (splitHeight <= 0) return;
      const startRatio = splitRatioRef.current;

      function onMove(moveEvent) {
        const ratio = startRatio + (moveEvent.clientY - startY) / splitHeight;
        const clamped = Math.min(MAX_SPLIT, Math.max(MIN_SPLIT, ratio));
        setSplitRatio(clamped);
      }
      function onUp() {
        document.removeEventListener("pointermove", onMove);
        document.removeEventListener("pointerup", onUp);
        document.body.style.userSelect = "";
        setSplitRatio((current) => {
          try {
            window.localStorage.setItem(SPLIT_STORAGE_KEY, String(current));
          } catch (_err) {
            // storage refused; the ratio still applies for this session
          }
          return current;
        });
      }
      document.body.style.userSelect = "none";
      document.addEventListener("pointermove", onMove);
      document.addEventListener("pointerup", onUp);
    },
    []
  );

  /**
   * Ask the preview for the bytes of the slots the config only references.
   *
   * Fired from the group being looked at, not from the whole design: a version
   * with sixty assets should not pay for the fifty-nine nobody is editing. One
   * request answers every row, and the answer is kept until the snapshot
   * changes.
   */
  useEffect(() => {
    if (!selectedGroup || props.isPreviewLoaded === false) return;
    if (previewAssetsRequestedRef.current === fileName) return;

    const needs = selectedGroup.fields.some((field) => {
      if (field.type !== "asset") return false;
      const entry = assetsMap[field.assetKey];
      return Boolean(entry) && !thumbnailFromEntry(entry);
    });
    if (!needs) return;

    previewAssetsRequestedRef.current = fileName;
    let cancelled = false;
    sendCommand(props.iFrameId, { type: "playengine:get-assets" }, 5000)
      .then((response) => {
        if (cancelled || !isMountedRef.current) return;
        const loaded = _.get(response, "assets", {}) || {};
        setPreviewAssetSources(
          _.mapValues(loaded, (asset) => _.get(asset, "source", null))
        );
      })
      .catch((err) => {
        // A thumbnail is a nicety; a preview that will not answer is not an
        // error the author needs to see. The badge stands in for the picture.
        console.warn(`[design] get-assets failed: ${err.message}`);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedGroup, assetsMap, fileName, props.iFrameId, props.isPreviewLoaded]);

  // ── Render ─────────────────────────────────────────────────────────────

  function renderEmptyState() {
    if (loading) {
      return (
        <div style={{ padding: 24, display: "flex", alignItems: "center", gap: 10 }}>
          <CircularProgress size={16} />
          <span style={{ fontSize: 13, color: "#888" }}>Loading design…</span>
        </div>
      );
    }
    if (loadError) {
      return (
        <div style={{ padding: "20px 16px" }}>
          <div style={{ fontSize: 13, color: "#e57373", marginBottom: 10 }}>{String(loadError)}</div>
          <Button size="small" variant="outlined" onClick={loadDesign} startIcon={<RefreshIcon />}>
            Retry
          </Button>
        </div>
      );
    }
    return (
      <div style={{ padding: "20px 16px", maxWidth: 460 }}>
        <div style={{ fontSize: 13, color: "#c9ccd2", marginBottom: 6 }}>
          This snapshot has no design yet.
        </div>
        <div style={{ fontSize: 12, color: "#8a8d93", lineHeight: 1.5, marginBottom: 14 }}>
          A design says what each variable IS — a colour, a position, a 0–1 knob — so this tab can
          show the right control instead of a text box. Generating one reads the snapshot and
          guesses a type for every value; you then delete what is not a setting and label the rest.
        </div>
        <Button
          size="small"
          variant="contained"
          color="primary"
          disabled={!canEdit || generating}
          onClick={handleGenerate}
          startIcon={generating ? <CircularProgress size={13} /> : <GenerateIcon />}
        >
          {generating ? "Generating…" : "Generate starter design"}
        </Button>
        {!canEdit && (
          <div style={{ fontSize: 11, color: "#6f7278", marginTop: 8 }}>
            You do not have permission to write to this flow.
          </div>
        )}
      </div>
    );
  }

  /**
   * A design this snapshot barely matches.
   *
   * Shown INSTEAD of the panel: below half, what is left after pruning is a
   * handful of rows another game happens to share, and a panel like that reads
   * as "this snapshot has almost no settings" rather than "this is the wrong
   * design".
   */
  function renderMismatch() {
    const percent = Math.round(coverage.ratio * 100);
    return (
      <div style={{ padding: "20px 16px", maxWidth: 460 }}>
        <div style={{ fontSize: 13, color: "#c9ccd2", marginBottom: 6 }}>
          This design does not match this snapshot.
        </div>
        <div style={{ fontSize: 12, color: "#8a8d93", lineHeight: 1.5, marginBottom: 14 }}>
          Only {coverage.present} of its {coverage.total} settings ({percent}%) exist here — the
          rest describe another snapshot&apos;s components. Pick the design this snapshot belongs
          to, or take the change back.
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {previousDesignId !== null && previousDesignId !== designId && (
            <Button
              size="small"
              variant="contained"
              color="primary"
              disabled={!canEdit || binding}
              onClick={() => handleBindDesign(previousDesignId)}
              style={{ textTransform: "none" }}
            >
              Undo
            </Button>
          )}
          <Button
            size="small"
            disabled={!canEdit || binding}
            onClick={() => handleBindDesign(null)}
            style={PANE_BUTTON_STYLE}
          >
            Detach
          </Button>
          <Button size="small" onClick={() => setIgnoreMismatch(true)} style={PANE_BUTTON_STYLE}>
            Open it anyway
          </Button>
        </div>
      </div>
    );
  }

  /** The inspector pane's own action, mirroring "Group" over the outliner. */
  function renderAddFieldButton() {
    if (!canEdit) return null;
    return (
      <div style={{ display: "flex", gap: 6, padding: "6px 5px 2px 10px" }}>
        <Button
          size="small"
          disabled={saving}
          onClick={() => setAddingField(true)}
          startIcon={<AddIcon style={{ fontSize: 14 }} />}
          style={PANE_BUTTON_STYLE}
        >
          Setting
        </Button>
      </div>
    );
  }

  function renderFields() {
    if (!selectedGroup) {
      return (
        <div style={{ padding: "20px 16px", fontSize: 13, color: "#888" }}>
          Select a group to edit its settings.
        </div>
      );
    }

    if (selectedGroup.fields.length === 0) {
      return (
        <>
          <InspectorHeader title={selectedGroup.label} />
          {renderAddFieldButton()}
          <SectionHeader title="Properties" />
          <div style={{ padding: "8px 12px", fontSize: 12, color: "#8a8d93" }}>
            {selectedGroup.groups.length > 0
              ? "A folder — its settings are in the groups inside it."
              : "No settings in this group yet."}
          </div>
        </>
      );
    }

    return (
      <>
        <InspectorHeader title={selectedGroup.label} />
        {renderAddFieldButton()}
        <SectionHeader title="Properties" />
        <div style={{ padding: "2px 4px 16px" }}>
          {selectedGroup.fields.map((field, fieldIndex) => {
            const groupNameMatches = matchingGroupNames.has(selectedGroup.id) ||
              allEntries.some((entry) => entry.group.id === selectedGroup.id &&
                entry.ancestors.some((id) => matchingGroupNames.has(id)));
            if (search.trim() && !groupNameMatches &&
              !matchesTabSearch(field.label, search) && !matchesTabSearch(field.path, search)) return null;
            const overridden = Object.keys(overrides).some(
              (candidate) => candidate === field.path || candidate.startsWith(`${field.path}.`)
            );
            // An asset row reads a slot, not a variable: its path is a sentinel
            // (`asset:<key>`) that no variable can answer, so "is it here?" has
            // to be asked of the asset map instead.
            const isAsset = field.type === "asset";
            const assetEntry = isAsset ? assetsMap[field.assetKey] || null : null;
            const assetType = _.get(assetEntry, "type", null);
            const assetThumbnail = isAsset
              ? thumbnailFromEntry(assetEntry) ||
                thumbnailFromEntry({ source: previewAssetSources[field.assetKey] })
              : null;
            return (
              <DesignFieldRenderer
                key={field.path}
                field={field}
                value={isAsset ? undefined : _.get(effectiveVariables, field.path)}
                present={isAsset ? Boolean(assetEntry) : _.has(variables, field.path)}
                disabled={!canEdit}
                isPortrait={isPortrait}
                assetEntry={assetEntry}
                assetThumbnail={assetThumbnail}
                assetBusy={busyAssetKey === field.assetKey}
                assetAccept={assetType ? getAcceptString(assetType) : "*"}
                // A pair (atlas, bitmap font) is replaced with TWO files at
                // once, which is the Assets tab's staging row; offering half of
                // it here would be a trap, so those slots keep the library and
                // the jump only.
                onAssetUpload={
                  isAsset && canUploadAssets && !isPairDepType(assetType) ? onAssetUpload : null
                }
                onAssetPickFromLibrary={
                  isAsset && canPickAssetsFromLibrary ? onAssetPickFromLibrary : null
                }
                onNavigateToAsset={props.onNavigateToAsset ? onNavigateToAsset : null}
                onChange={onFieldChange}
                onCommit={onFieldCommit}
                language={selectedLanguage}
                isOverrideMode={isOverrideLanguage(selectedLanguage)}
                isOverridden={overridden}
                onRevert={onFieldRevert}
                // `allEntries` is memoised on the design, so a row's memo still
                // holds across an edit and breaks only when the tree changes —
                // which is exactly when the move menu's contents are stale.
                groupEntries={allEntries}
                currentGroupId={selectedGroup.id}
                schemaOptions={schemaOptionsForPath(engineOptions, field.path)}
                onEditField={onEditField}
                onEditFieldOptions={onEditFieldOptions}
                onReorderField={onReorderField}
                canMoveUp={fieldIndex > 0}
                canMoveDown={fieldIndex < selectedGroup.fields.length - 1}
                onMoveField={onMoveField}
                onRemoveField={onRemoveField}
              />
            );
          })}
        </div>
      </>
    );
  }

  return (
    <div
      ref={rootRef}
      className="design-props-panel"
      style={{
        display: "flex",
        flexDirection: "column",
        height: availHeight,
        minHeight: 0,
        backgroundColor: PANEL_BG,
      }}
    >
      <style>{DESIGN_PANEL_CSS}</style>
      <PlayEngineTabSearch value={search} onChange={setSearch} />

      {/* Toolbar
          Wraps rather than overflowing: the row ends with the language picker
          and the reload button, and in a narrow panel a single no-wrap line
          pushed both out of sight — the language picker could only be reached
          by widening the panel. */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 10,
          rowGap: 6,
          padding: "8px 10px",
          flexShrink: 0,
          backgroundColor: BAR_BG,
          borderBottom: `1px solid ${SECTION_DIVIDER}`,
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 600, color: "#e8e8e8" }}>
          {design && design.title ? design.title : "Design"}
        </span>
        {/* Which design this snapshot uses, and the way to change it. A design
            is a separate file shared by every snapshot a flow generates from one
            template, so repointing a snapshot is picking a file — not editing
            one. */}
        {canBindDesign ? (
          <>
            <Select
              size="small"
              displayEmpty
              value={isBoundDesignKnown ? designId : ""}
              disabled={!canEdit || binding || loading}
              onChange={(event) => handleBindDesign(event.target.value || null)}
              MenuProps={{ PaperProps: { style: { backgroundColor: PANEL_BG, color: "#e8e8e8" } } }}
              style={{ fontSize: 12, color: "#fff", minWidth: 150, maxWidth: 240 }}
            >
              <MenuItem value="" style={{ color: "#8a8d93", fontSize: 12 }}>
                No design
              </MenuItem>
              {/* A binding pointing at a file that is not here has to be visible
                  — it is the reason the tab looks empty. */}
              {designId && !isBoundDesignKnown ? (
                <MenuItem disabled style={{ fontSize: 11, color: "#c9a25b" }}>
                  {designId} (not on this flow)
                </MenuItem>
              ) : null}

              {offeredDesigns.map((entry) => {
                const fits = designsByFit.fitting.includes(entry);
                return (
                  <MenuItem
                    key={entry.designId}
                    value={entry.designId}
                    style={{ color: fits ? "#eee" : "#8a8d93", fontSize: 12 }}
                  >
                    {entry.title && entry.title !== entry.designId
                      ? `${entry.title} — ${entry.designId}`
                      : entry.designId}
                    {entry.isReadable === false ? " (unreadable)" : ""}
                    {_.isFinite(entry.matchRatio) && entry.matchableFields > 0
                      ? ` · ${entry.matchingFields}/${entry.matchableFields}`
                      : ""}
                    {entry.sameShapeCount > 1 ? ` · +${entry.sameShapeCount - 1} same` : ""}
                  </MenuItem>
                );
              })}

            </Select>
            {hiddenDesignCount > 0 ? (
              <Tooltip
                title={
                  showAllDesigns
                    ? "List only the designs this snapshot matches"
                    : `${hiddenDesignCount} design${hiddenDesignCount === 1 ? "" : "s"} on this flow describe other snapshots. The bar is a guess — show them if the right one is missing.`
                }
                arrow
              >
                <span>
                  <Button
                    size="small"
                    onClick={() => setShowAllDesigns((current) => !current)}
                    style={PANE_BUTTON_STYLE}
                  >
                    {showAllDesigns ? "Matching only" : `Show all (${hiddenDesignCount})`}
                  </Button>
                </span>
              </Tooltip>
            ) : null}
            {designId ? (
              <Tooltip title="Stop this snapshot using that design. The design file itself stays." arrow>
                <span>
                  <Button
                    size="small"
                    disabled={!canEdit || binding}
                    onClick={() => handleBindDesign(null)}
                    style={PANE_BUTTON_STYLE}
                  >
                    Detach
                  </Button>
                </span>
              </Tooltip>
            ) : null}
            {previousDesignId !== null && previousDesignId !== designId ? (
              <Tooltip
                title={
                  previousDesignId
                    ? `Go back to the "${previousDesignId}" design`
                    : "Go back to no design"
                }
                arrow
              >
                <span>
                  <Button
                    size="small"
                    disabled={!canEdit || binding}
                    onClick={() => handleBindDesign(previousDesignId)}
                    style={PANE_BUTTON_STYLE}
                  >
                    Undo
                  </Button>
                </span>
              </Tooltip>
            ) : null}
            {binding ? <CircularProgress size={13} /> : null}
          </>
        ) : (
          designId && (
            <span style={{ fontSize: 11, color: "#7f838a", fontFamily: "monospace" }}>{designId}</span>
          )
        )}
        {design && (
          <span style={{ fontSize: 11, color: "#7f838a", whiteSpace: "nowrap" }}>
            {shownGroupCount === groupCount
              ? `${groupCount} groups`
              : `${shownGroupCount}/${groupCount} groups`}
            {" · "}
            {shownFieldCount === fieldCount
              ? `${fieldCount} fields`
              : `${shownFieldCount}/${fieldCount} fields`}
          </span>
        )}
        {design && shownFieldCount !== fieldCount && (
          <Tooltip
            title={
              showMissingFields
                ? "Hide the settings this snapshot has no value for"
                : `${fieldCount - shownFieldCount} settings in this design are not in this snapshot. Show them to edit the design for the snapshots that do have them.`
            }
            arrow
          >
            <span>
              <Button
                size="small"
                onClick={() => setShowMissingFields((current) => !current)}
                style={PANE_BUTTON_STYLE}
              >
                {showMissingFields ? "Hide unused" : "Show unused"}
              </Button>
            </span>
          </Tooltip>
        )}

        <span style={{ flex: "1 1 0", minWidth: 0 }} />

        {availableLanguages.length > 1 && (
          <Select
            size="small"
            value={selectedLanguage}
            onChange={(event) => handleLanguageSwitch(event.target.value)}
            // Same surface the Scene Editor's language bar opens on — a menu
            // renders in a portal, where the panel's own CSS cannot reach it.
            MenuProps={{ PaperProps: { style: { backgroundColor: PANEL_BG, color: "#e8e8e8" } } }}
            style={{ fontSize: 12, color: "#fff", minWidth: 64 }}
          >
            {availableLanguages.map((language) => (
              <MenuItem key={language} value={language} style={{ color: "#eee", fontSize: 12 }}>
                {language}
              </MenuItem>
            ))}
          </Select>
        )}

        {saving && <CircularProgress size={13} />}

        <Tooltip title="Reload the design from disk" arrow>
          <span>
            <Button size="small" onClick={loadDesign} disabled={loading} style={{ minWidth: 0 }}>
              <RefreshIcon style={{ fontSize: 16 }} />
            </Button>
          </span>
        </Tooltip>
      </div>

      {problems.length > 0 && (
        <div
          style={{
            flexShrink: 0,
            padding: "6px 10px",
            fontSize: 11,
            color: "#c9a25b",
            backgroundColor: "rgba(201, 162, 91, 0.08)",
            borderBottom: `1px solid ${SECTION_DIVIDER}`,
          }}
        >
          {problems.length} problem{problems.length === 1 ? "" : "s"} in this design — first:{" "}
          {problems[0].where}: {problems[0].message}
        </div>
      )}

      {!design ? (
        <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>{renderEmptyState()}</div>
      ) : isMismatched ? (
        <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>{renderMismatch()}</div>
      ) : (
        <>
          {/* Both panes are flex ratios of what is left after the chrome, so the
              split means the same thing however tall the toolbar and the banner
              happen to be — the Scene Editor's arrangement. */}
          <div
            ref={outlinerPaneRef}
            style={{
              flexGrow: splitRatio,
              flexShrink: 1,
              flexBasis: 0,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <PaneCaption title="Groups" />

            <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "4px 0" }}>
              {/* The pane's own action, where the Scene Editor keeps "Category" —
                  inside the tree it acts on, rather than in the toolbar above,
                  which names the design as a whole. */}
              {canEdit && (
                <div style={{ display: "flex", gap: 6, padding: "4px 5px 4px 10px" }}>
                  <Button
                    size="small"
                    disabled={saving}
                    onClick={() => handleAddGroup(null)}
                    startIcon={<AddIcon style={{ fontSize: 14 }} />}
                    style={PANE_BUTTON_STYLE}
                  >
                    Group
                  </Button>
                  <Tooltip
                    title="Add groups for the settings this snapshot has that the design does not describe"
                    arrow
                  >
                    <span>
                      <Button
                        size="small"
                        disabled={saving || mergingSnapshotSettings}
                        onClick={handleMergeSnapshotSettings}
                        startIcon={
                          mergingSnapshotSettings ? (
                            <CircularProgress size={13} />
                          ) : (
                            <GenerateIcon style={{ fontSize: 14 }} />
                          )
                        }
                        style={PANE_BUTTON_STYLE}
                      >
                        From snapshot
                      </Button>
                    </span>
                  </Tooltip>
                </div>
              )}

              <DesignOutliner
                entries={visibleEntries}
                selectedId={selectedGroup ? selectedGroup.id : null}
                expandedIds={search.trim() ? matchingGroupIds : expandedIds}
                canEdit={canEdit && !saving && !search.trim()}
                onSelect={setSelectedGroupId}
                onToggle={toggleExpanded}
                onMove={handleMove}
                onAddChild={handleAddGroup}
                onRename={(id, label) => setRenaming({ id, label })}
                onRemove={handleRemoveGroup}
              />
              {search.trim() && matchingEntries.length === 0 && (
                <div style={{ padding: 12, color: "#888", fontSize: 12 }}>No knobs match this search.</div>
              )}
            </div>
          </div>

          <SplitHandle onPointerDown={startDividerDrag} />

          <div
            ref={inspectorPaneRef}
            style={{
              flexGrow: 1 - splitRatio,
              flexShrink: 1,
              flexBasis: 0,
              minHeight: 0,
              overflowY: "auto",
              backgroundColor: PANEL_BG,
            }}
          >
            {renderFields()}
          </div>
        </>
      )}

      <DialogAddDesignField
        open={addingField || Boolean(editingField)}
        groupEntries={allEntries}
        defaultGroupId={selectedGroupId}
        variables={variables}
        assets={assetsMap}
        isPathTaken={isPathTaken}
        editingField={editingField ? editingField.field : null}
        editingGroupId={editingField ? editingField.groupId : null}
        onClose={() => {
          setAddingField(false);
          setEditingFieldPath(null);
        }}
        onSubmit={editingField ? handleUpdateField : handleAddField}
      />

      <Dialog
        open={Boolean(optionsDraft)}
        onClose={() => setOptionsDraft(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ style: { backgroundColor: PANEL_BG } }}
      >
        <DialogTitle>Allowed values</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={4}
            maxRows={14}
            size="small"
            placeholder={"none\npulse\nclick"}
            value={optionsDraft ? optionsDraft.text : ""}
            onChange={(event) => {
              const text = event.target.value;
              setOptionsDraft((current) => ({ ...current, text }));
            }}
            onKeyDown={(event) => event.stopPropagation()}
            InputProps={{ style: { fontFamily: "monospace", fontSize: 12 } }}
            helperText={
              "One value per line. Write value = Label to show a different label. " +
              "Leave it empty to go back to a text box."
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOptionsDraft(null)}>Cancel</Button>
          <Button color="primary" onClick={handleOptionsCommit}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Same paper as the dialog above it and as the snapshot editor's own —
          these two open over this panel, and one of them on MUI's default grey
          would be the only surface in the tab that is not the tab's. */}
      <Dialog
        open={Boolean(removingGroup)}
        onClose={() => setRemovingGroup(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ style: { backgroundColor: PANEL_BG } }}
      >
        <DialogTitle>Delete this group?</DialogTitle>
        <DialogContent>
          <div style={{ fontSize: 13, color: "#c9ccd2", lineHeight: 1.5 }}>
            &quot;{_.get(removingGroup, "label")}&quot; holds{" "}
            {_.get(removingGroup, "contents.fields", 0)} setting
            {_.get(removingGroup, "contents.fields", 0) === 1 ? "" : "s"}
            {_.get(removingGroup, "contents.groups", 0) > 0
              ? ` in ${_.get(removingGroup, "contents.groups", 0)} group${
                _.get(removingGroup, "contents.groups", 0) === 1 ? "" : "s"
              } inside it`
              : ""}
            . All of it goes.
          </div>
          <div style={{ fontSize: 12, color: "#8a8d93", lineHeight: 1.5, marginTop: 10 }}>
            This removes rows from the design, not values from the snapshot: every one of them
            keeps its value and stays editable in Custom Variables.
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemovingGroup(null)}>Cancel</Button>
          <Button style={{ color: "#e57373" }} onClick={confirmRemoveGroup}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(renaming)}
        onClose={() => setRenaming(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ style: { backgroundColor: PANEL_BG } }}
      >
        <DialogTitle>Rename group</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Name"
            value={renaming ? renaming.label : ""}
            onChange={(event) => {
              // Read BEFORE the updater runs: React 16 pools synthetic events
              // and nullifies `target` once the handler returns, so reaching for
              // it inside the callback threw and took the panel down with it.
              const label = event.target.value;
              setRenaming((current) => ({ ...current, label }));
            }}
            onKeyDown={(event) => {
              event.stopPropagation();
              if (event.key === "Enter") handleRenameCommit();
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenaming(null)}>Cancel</Button>
          <Button color="primary" onClick={handleRenameCommit}>
            Rename
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
